import { useState } from "react";
import { Loader2, Pencil } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { updateSchool } from "@/services/systemAdminService";
import type { LicenseInventoryRow } from "@/types/systemAdmin";

interface EditSchoolDialogProps {
  school: LicenseInventoryRow;
  onUpdated: () => Promise<void>;
}

export const EditSchoolDialog = ({ school, onUpdated }: EditSchoolDialogProps) => {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(school.schoolName ?? "");
  const [busy, setBusy] = useState(false);

  const openDialog = () => {
    setName(school.schoolName ?? "");
    setOpen(true);
  };

  const handleSave = async () => {
    if (!name.trim() || !school.schoolId) return;

    setBusy(true);
    try {
      await updateSchool(school.schoolId, name);
      toast({ title: "School updated" });
      setOpen(false);
      await onUpdated();
    } catch (err) {
      toast({
        title: "Could not update school",
        description: err instanceof Error ? err.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        aria-label={`Edit ${school.schoolName ?? "school"}`}
        onClick={openDialog}
      >
        <Pencil className="h-4 w-4" aria-hidden />
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit school</DialogTitle>
            <DialogDescription>Update this school&apos;s name.</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-2">
            <label htmlFor="edit-school-name" className="text-sm text-gray-600">
              Name
            </label>
            <Input id="edit-school-name" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <DialogFooter>
            <Button
              type="button"
              disabled={busy || !name.trim()}
              onClick={() => void handleSave()}
            >
              {busy ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                  Saving…
                </>
              ) : (
                "Save"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
